/**
 * «¿Esta fila es de una finca suya?», en un solo sitio.
 *
 * POR QUÉ EXISTE ESTO. El recorte por finca se escribió a mano en cada pantalla
 * —un `filter` de tres líneas repetido— y el usuario fue encontrando las que
 * faltaban de una en una, en cuatro rondas: primero el Planificador, luego el
 * calendario, luego el Registro, luego el Panel, luego Plagas y el Registro de
 * inventario. No es que se olvidaran: es que no había DÓNDE acordarse. Cada
 * pantalla nueva nacía sin recorte y nada lo notaba.
 *
 * Con una función compartida hay un nombre que buscar, y el guardián
 * (`alcancePorFinca.guard.test.ts`) puede barrer TODOS los ficheros que
 * consultan tablas con finca y exigir que la llamen, con una lista corta de
 * exenciones justificadas. Deja de depender de la memoria de nadie.
 *
 * LAS DOS REGLAS, las mismas que en SQL (`location_in_manager_scope`):
 *
 *   · Sin recorte (`fincas === null`) no se toca nada. Es lo que mantiene el
 *     valor por defecto de siempre para admin, perito y para toda empresa que
 *     no use el alcance.
 *   · Una fila SIN finca se queda. Una tarea sin invernadero es trabajo general
 *     y no es de nadie; esconderla sería quitarle trabajo a quien sí lo tiene.
 *
 * Y la finca CONCEDE, el sector RESTA: un sector quitado dentro de una finca
 * suya no se ve, pero un sector jamás concede por su cuenta.
 */

type ConFinca = { location_id?: string | null } | null;

/**
 * Las formas en que una fila dice de dónde es, según cómo esté hecho el join.
 *
 * Las funciones de abajo aceptan `unknown` y leen a través de este tipo a
 * propósito: las filas reales vienen de una decena de `select` distintos, cada
 * uno con sus campos, y atarlas a una interfaz obligaba a que TODAS encajaran —
 * lo que en la práctica significaba pelearse con el comprobador en cada sitio
 * en vez de aplicar la regla. La comprobación de verdad es la del `select`:
 * si no pide `location_id`, no hay filtro que valga, y eso no lo puede ver el
 * compilador.
 */
export interface FilaConSitio {
  block_id?: string | null;
  location_id?: string | null;
  greenhouse_id?: string | null;
  greenhouses?: ConFinca;
  /** Alias frecuente: `greenhouse:greenhouses(...)`. */
  greenhouse?: ConFinca;
  /** Cuando se llega al invernadero a través del sector. */
  blocks?: { greenhouses?: ConFinca } | null;
  block?: { greenhouses?: ConFinca } | null;
}

/**
 * La finca de una fila, mire por donde mire.
 *
 * `undefined` significa «no lo dice», que NO es lo mismo que «no tiene»: si la
 * consulta no pidió `location_id`, aquí no puede aparecer. Por eso las
 * consultas que se recortan tienen que traerlo — y por eso conviene revisar el
 * `select` antes de dar por bueno un filtro que no filtra nada.
 */
export function fincaDeFila(fila: unknown): string | null | undefined {
  const r = (fila ?? {}) as FilaConSitio;
  return (
    r.greenhouses?.location_id ??
    r.greenhouse?.location_id ??
    r.blocks?.greenhouses?.location_id ??
    r.block?.greenhouses?.location_id ??
    r.location_id ??
    null
  );
}

/**
 * Deja solo lo que este usuario puede ver.
 *
 * @param fincas   `null` = sin recorte. Viene de `useLocationScope().allowedLocationIds`.
 * @param quitados sectores restados dentro de una finca suya (`excludedBlockIds`).
 */
export function filtrarPorAlcance<T>(
  rows: T[] | null | undefined,
  fincas: Set<string> | null | undefined,
  quitados?: Set<string> | null,
): T[] {
  const lista = rows ?? [];
  if (!fincas) return lista;
  return lista.filter((r) => enAlcance(r, fincas, quitados));
}

/** La misma regla para una fila suelta, cuando no hay lista que filtrar. */
export function enAlcance(
  fila: unknown,
  fincas: Set<string> | null | undefined,
  quitados?: Set<string> | null,
): boolean {
  if (!fincas) return true;
  const finca = fincaDeFila(fila);
  if (finca && !fincas.has(finca)) return false;
  const bloque = ((fila ?? {}) as FilaConSitio).block_id;
  return !(bloque && quitados?.has(bloque));
}

/**
 * El alcance como texto, para meterlo en la `queryKey` de react-query.
 *
 * Un `Set` no vale de clave: react-query compara las claves serializándolas y
 * dos `Set` distintos se serializan igual («{}»), así que al cambiarle el
 * alcance a alguien la consulta NO se volvería a lanzar y seguiría enseñando lo
 * de antes hasta recargar la página.
 */
export function claveAlcance(
  fincas: Set<string> | null | undefined,
  quitados?: Set<string> | null,
): string {
  if (!fincas) return "todas";
  const f = [...fincas].sort().join(",");
  const q = quitados && quitados.size > 0 ? `-${[...quitados].sort().join(",")}` : "";
  return f + q;
}
